import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usersAPI } from '../../services/api';
import { 
  FiCamera, FiUser, FiMail, FiPhone, FiBriefcase, FiMapPin, 
  FiGlobe, FiInstagram, FiTwitter, FiLinkedin, FiGithub, 
  FiLock, FiSave, FiX, FiAlertCircle 
} from 'react-icons/fi';
import { HiOutlineBuildingOffice2 } from 'react-icons/hi2';
import AvatarPickerModal from '../../components/AvatarPickerModal/AvatarPickerModalStatic';
import toast from 'react-hot-toast';
import { resolveMediaUrl } from '../../utils/media';
import './EditProfile.css';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic'); // basic, professional, social, security
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatar, setAvatar] = useState(null); // { options, dataUrl }
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  
  // Formulario de información básica
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    bio: '',
    location: '',
    website: ''
  });

  // Formulario de información profesional
  const [professionalData, setProfessionalData] = useState({
    job_title: '',
    organization: '',
    department: '',
    academic_title: '',
    specialization: ''
  });

  // Formulario de redes sociales
  const [socialData, setSocialData] = useState({
    instagram: '',
    twitter: '',
    linkedin: '',
    github: ''
  });

  // Formulario de seguridad
  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Avatar
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        location: user.location || '',
        website: user.website || ''
      });

      setProfessionalData({
        job_title: user.job_title || '',
        organization: user.organization || '',
        department: user.department || '',
        academic_title: user.academic_title || '',
        specialization: user.specialization || ''
      });

      setSocialData({
        instagram: user.instagram || '',
        twitter: user.twitter || '',
        linkedin: user.linkedin || '',
        github: user.github || ''
      });

      setAvatarPreview(resolveMediaUrl(user.avatar) || '/avatars/default-avatar.png');
    }
  }, [user]);

  const handleInputChange = (e, setData) => {
    const { name, value } = e.target;
    setData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarSelect = async (selectedAvatar) => {
    console.log('Avatar selected:', selectedAvatar);
    
    try {
      if (selectedAvatar.uploadedPhoto) {
        // Avatar subido por el usuario
        console.log('Processing uploaded photo...');
        setAvatarPreview(selectedAvatar.uploadedPhoto);
        setAvatar(selectedAvatar);
        // Convertir dataUrl a archivo
        const res = await fetch(selectedAvatar.uploadedPhoto);
        const blob = await res.blob();
        const file = new File([blob], 'avatar.png', { type: 'image/png' });
        console.log('Created file from uploaded photo:', file);
        setSelectedFile(file);
      } else if (selectedAvatar.url) {
        // Avatar predefinido - guardar la URL directamente
        console.log('Selected predefined avatar:', selectedAvatar.url);
        setAvatarPreview(selectedAvatar.url);
        setAvatar({ url: selectedAvatar.url, isPredefined: true });
        setSelectedFile(null); // No hay archivo, usaremos la URL
      }
      setAvatarModalOpen(false);
    } catch (error) {
      console.error('Error al procesar avatar:', error);
      toast.error('Error al procesar la imagen');
      setAvatarModalOpen(false);
    }
  };

  const handleSaveBasicInfo = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    if (!formData.username.trim()) {
      toast.error('El nombre de usuario es requerido');
      return;
    }

    if (formData.username.length < 3) {
      toast.error('El nombre de usuario debe tener al menos 3 caracteres');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('El correo electrónico es requerido');
      return;
    }

    try {
      setLoading(true);
      
      let newAvatarUrl = user.avatar; // Mantener el avatar actual por defecto
      
      // Si hay un archivo de imagen, subirlo primero
      if (selectedFile) {
        console.log('Uploading avatar file...');
        const avatarFormData = new FormData();
        avatarFormData.append('avatar', selectedFile);
        
        const avatarResponse = await usersAPI.uploadAvatar(avatarFormData);
        console.log('Avatar uploaded:', avatarResponse);
        
        if (avatarResponse.data.avatar) {
          newAvatarUrl = avatarResponse.data.avatar;
        }
      } 
      // Si hay un avatar predefinido seleccionado, usar su URL
      else if (avatar && avatar.isPredefined && avatar.url) {
        console.log('Using predefined avatar:', avatar.url);
        newAvatarUrl = avatar.url;
      }
      
      // Actualizar el perfil (incluyendo el avatar si cambió)
      const updateData = { ...formData };
      if (newAvatarUrl !== user.avatar) {
        updateData.avatar = newAvatarUrl;
      }
      
      console.log('Updating profile with data:', updateData);
      const response = await usersAPI.updateProfile(updateData);
      
      if (response.data.user) {
        updateUser(response.data.user);
        setSelectedFile(null);
        setAvatar(null);
        toast.success('Cambios guardados exitosamente');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Error al actualizar el perfil');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendPhoneVerification = async () => {
    if (!formData.phone || formData.phone.trim().length < 10) {
      toast.error('Ingresa un número de teléfono válido');
      return;
    }

    try {
      setVerifyingPhone(true);
      // Aquí iría la llamada a la API para enviar el SMS
      await usersAPI.sendPhoneVerification(formData.phone);
      setShowPhoneVerification(true);
      toast.success('Código enviado a tu teléfono');
    } catch (error) {
      console.error('Error sending verification:', error);
      toast.error('Error al enviar el código de verificación');
    } finally {
      setVerifyingPhone(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!phoneVerificationCode || phoneVerificationCode.length !== 6) {
      toast.error('Ingresa un código válido de 6 dígitos');
      return;
    }

    try {
      setVerifyingPhone(true);
      // Aquí iría la llamada a la API para verificar el código
      await usersAPI.verifyPhone(formData.phone, phoneVerificationCode);
      toast.success('Teléfono verificado exitosamente');
      setShowPhoneVerification(false);
      setPhoneVerificationCode('');
      updateUser({ phone_verified: true });
    } catch (error) {
      console.error('Error verifying phone:', error);
      toast.error(error.response?.data?.error || 'Código inválido');
    } finally {
      setVerifyingPhone(false);
    }
  };

  const handleSaveProfessionalInfo = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      // Incluir campos básicos requeridos
      const updateData = {
        name: formData.name,
        username: formData.username,
        email: formData.email,
        ...professionalData
      };
      
      const response = await usersAPI.updateProfile(updateData);
      
      if (response.data.user) {
        updateUser(response.data.user);
        toast.success('Información profesional actualizada');
      }
    } catch (error) {
      console.error('Error updating professional info:', error);
      toast.error('Error al actualizar la información profesional');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSocialLinks = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      // Incluir campos básicos requeridos
      const updateData = {
        name: formData.name,
        username: formData.username,
        email: formData.email,
        ...socialData
      };
      
      const response = await usersAPI.updateProfile(updateData);
      
      if (response.data.user) {
        updateUser(response.data.user);
        toast.success('Redes sociales actualizadas');
      }
    } catch (error) {
      console.error('Error updating social links:', error);
      toast.error('Error al actualizar las redes sociales');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!securityData.currentPassword) {
      toast.error('Ingresa tu contraseña actual');
      return;
    }

    if (!securityData.newPassword) {
      toast.error('Ingresa una nueva contraseña');
      return;
    }

    if (securityData.newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (securityData.newPassword !== securityData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (securityData.currentPassword === securityData.newPassword) {
      toast.error('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    try {
      setLoading(true);
      await usersAPI.changePassword({
        currentPassword: securityData.currentPassword,
        newPassword: securityData.newPassword
      });
      
      toast.success('Contraseña actualizada exitosamente');
      setSecurityData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Error al cambiar la contraseña');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-profile-container">
      <div className="edit-profile-header">
        <div className="header-content">
          <h1>Editar Perfil</h1>
          <button className="btn-close" onClick={() => navigate(-1)}>
            <FiX /> Cancelar
          </button>
        </div>
      </div>

      <div className="edit-profile-content">
        {/* Sidebar con avatar y tabs */}
        <aside className="edit-profile-sidebar">
          <div className="avatar-section">
            <div className="avatar-upload">
              <img 
                src={avatarPreview} 
                alt="Avatar" 
                className="avatar-preview"
              />
              <button 
                className="btn-change-avatar"
                onClick={() => setAvatarModalOpen(true)}
              >
                <FiCamera />
              </button>
            </div>
            
            {(selectedFile || avatar) && (
              <div className="avatar-pending">
                <FiAlertCircle className="icon-warning" />
                <small>Foto pendiente. Guarda los cambios para aplicar.</small>
              </div>
            )}

            <p className="avatar-help">
              JPG, PNG o GIF. Máx. 5MB
            </p>
          </div>

          <nav className="edit-tabs">
            <button
              className={`edit-tab ${activeTab === 'basic' ? 'active' : ''}`}
              onClick={() => setActiveTab('basic')}
            >
              <FiUser /> Información Básica
            </button>
            <button
              className={`edit-tab ${activeTab === 'professional' ? 'active' : ''}`}
              onClick={() => setActiveTab('professional')}
            >
              <FiBriefcase /> Información Profesional
            </button>
            <button
              className={`edit-tab ${activeTab === 'social' ? 'active' : ''}`}
              onClick={() => setActiveTab('social')}
            >
              <FiGlobe /> Redes Sociales
            </button>
            <button
              className={`edit-tab ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              <FiLock /> Seguridad
            </button>
          </nav>
        </aside>

        {/* Contenido principal */}
        <main className="edit-profile-main">
          {/* Tab: Información Básica */}
          {activeTab === 'basic' && (
            <form onSubmit={handleSaveBasicInfo} className="edit-form">
              <h2>Información Básica</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FiUser /> Nombre Completo *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange(e, setFormData)}
                    placeholder="Tu nombre completo"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FiUser /> Nombre de Usuario *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={(e) => handleInputChange(e, setFormData)}
                    placeholder="@usuario"
                    required
                  />
                  <small>Mínimo 3 caracteres. Solo letras, números y guiones.</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FiMail /> Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange(e, setFormData)}
                    placeholder="tu@email.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FiPhone /> Teléfono {user?.phone_verified && <span className="verified-badge">✓ Verificado</span>}
                  </label>
                  <div className="phone-verification-group">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange(e, setFormData)}
                      placeholder="+1234567890"
                      disabled={showPhoneVerification}
                    />
                    {!user?.phone_verified && formData.phone && (
                      <button
                        type="button"
                        className="btn-verify"
                        onClick={handleSendPhoneVerification}
                        disabled={verifyingPhone || showPhoneVerification}
                      >
                        {verifyingPhone ? 'Enviando...' : 'Verificar'}
                      </button>
                    )}
                  </div>
                  {showPhoneVerification && (
                    <div className="verification-code-group">
                      <input
                        type="text"
                        value={phoneVerificationCode}
                        onChange={(e) => setPhoneVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Código de 6 dígitos"
                        maxLength="6"
                      />
                      <button
                        type="button"
                        className="btn-verify-code"
                        onClick={handleVerifyPhone}
                        disabled={verifyingPhone || phoneVerificationCode.length !== 6}
                      >
                        {verifyingPhone ? 'Verificando...' : 'Confirmar'}
                      </button>
                      <button
                        type="button"
                        className="btn-cancel-verification"
                        onClick={() => {
                          setShowPhoneVerification(false);
                          setPhoneVerificationCode('');
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>
                  <FiUser /> Biografía
                </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={(e) => handleInputChange(e, setFormData)}
                  placeholder="Cuéntanos sobre ti..."
                  rows="4"
                  maxLength="500"
                />
                <small>{formData.bio.length}/500 caracteres</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FiMapPin /> Ubicación
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={(e) => handleInputChange(e, setFormData)}
                    placeholder="Ciudad, País"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FiGlobe /> Sitio Web
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={(e) => handleInputChange(e, setFormData)}
                    placeholder="https://tuwebsite.com"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-save" disabled={loading}>
                  <FiSave /> {loading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          )}

          {/* Tab: Información Profesional */}
          {activeTab === 'professional' && (
            <form onSubmit={handleSaveProfessionalInfo} className="edit-form">
              <h2>Información Profesional</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FiBriefcase /> Cargo / Puesto
                  </label>
                  <input
                    type="text"
                    name="job_title"
                    value={professionalData.job_title}
                    onChange={(e) => handleInputChange(e, setProfessionalData)}
                    placeholder="Ej: Ingeniero de Software"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <HiOutlineBuildingOffice2 /> Organización
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={professionalData.organization}
                    onChange={(e) => handleInputChange(e, setProfessionalData)}
                    placeholder="Ej: Universidad Nacional"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <HiOutlineBuildingOffice2 /> Departamento
                </label>
                <input
                  type="text"
                  name="department"
                  value={professionalData.department}
                  onChange={(e) => handleInputChange(e, setProfessionalData)}
                  placeholder="Ej: Departamento de Ingeniería"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <FiUser /> Título Académico
                  </label>
                  <input
                    type="text"
                    name="academic_title"
                    value={professionalData.academic_title}
                    onChange={(e) => handleInputChange(e, setProfessionalData)}
                    placeholder="Ej: PhD, MSc, Lic."
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FiBriefcase /> Especialización
                  </label>
                  <input
                    type="text"
                    name="specialization"
                    value={professionalData.specialization}
                    onChange={(e) => handleInputChange(e, setProfessionalData)}
                    placeholder="Ej: Inteligencia Artificial"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-save" disabled={loading}>
                  <FiSave /> {loading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          )}

          {/* Tab: Redes Sociales */}
          {activeTab === 'social' && (
            <form onSubmit={handleSaveSocialLinks} className="edit-form">
              <h2>Redes Sociales</h2>
              
              <div className="form-group">
                <label>
                  <FiInstagram /> Instagram
                </label>
                <input
                  type="text"
                  name="instagram"
                  value={socialData.instagram}
                  onChange={(e) => handleInputChange(e, setSocialData)}
                  placeholder="@usuario"
                />
              </div>

              <div className="form-group">
                <label>
                  <FiTwitter /> Twitter / X
                </label>
                <input
                  type="text"
                  name="twitter"
                  value={socialData.twitter}
                  onChange={(e) => handleInputChange(e, setSocialData)}
                  placeholder="@usuario"
                />
              </div>

              <div className="form-group">
                <label>
                  <FiLinkedin /> LinkedIn
                </label>
                <input
                  type="url"
                  name="linkedin"
                  value={socialData.linkedin}
                  onChange={(e) => handleInputChange(e, setSocialData)}
                  placeholder="https://linkedin.com/in/usuario"
                />
              </div>

              <div className="form-group">
                <label>
                  <FiGithub /> GitHub
                </label>
                <input
                  type="text"
                  name="github"
                  value={socialData.github}
                  onChange={(e) => handleInputChange(e, setSocialData)}
                  placeholder="@usuario"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-save" disabled={loading}>
                  <FiSave /> {loading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          )}

          {/* Tab: Seguridad */}
          {activeTab === 'security' && (
            <form onSubmit={handleChangePassword} className="edit-form">
              <h2>Cambiar Contraseña</h2>
              
              <div className="alert-info">
                <FiAlertCircle />
                <p>Tu contraseña debe tener al menos 6 caracteres.</p>
              </div>

              <div className="form-group">
                <label>
                  <FiLock /> Contraseña Actual *
                </label>
                <input
                  type="password"
                  name="currentPassword"
                  value={securityData.currentPassword}
                  onChange={(e) => handleInputChange(e, setSecurityData)}
                  placeholder="Ingresa tu contraseña actual"
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <FiLock /> Nueva Contraseña *
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={securityData.newPassword}
                  onChange={(e) => handleInputChange(e, setSecurityData)}
                  placeholder="Ingresa tu nueva contraseña"
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <FiLock /> Confirmar Nueva Contraseña *
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={securityData.confirmPassword}
                  onChange={(e) => handleInputChange(e, setSecurityData)}
                  placeholder="Confirma tu nueva contraseña"
                  required
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-save" disabled={loading}>
                  <FiSave /> {loading ? 'Cambiando...' : 'Cambiar Contraseña'}
                </button>
              </div>
            </form>
          )}
        </main>
      </div>

      {/* Modal de selección de avatar */}
      <AvatarPickerModal
        isOpen={avatarModalOpen}
        onSelect={handleAvatarSelect}
        onClose={() => setAvatarModalOpen(false)}
        value={avatar}
        initialTab="pick"
      />
    </div>
  );
};

export default EditProfile;
